import React, { useEffect, useState, useRef } from "react";
import Wrapper from "../../../components/Wrapper";
import Typography from "@mui/material/Typography";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Button,
  Paper,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Alert,
} from "@mui/material";
import title from "../../../components/title";
import { GetOneAgreement } from "../../../redux/reducer/GetOneAgreement";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import PrintIcon from "@mui/icons-material/Print";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawIcon from "@mui/icons-material/Draw";
import { Link } from "react-router-dom";
import { landlordConditionOptions } from "../MyProperty/validationSchema";
import { resolveLatePolicy, describeLatePolicy } from "../../../utils/latePolicy";

export default function Agreements() {
  title("Agreement");
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);

  const [data, setData] = useState([]);
  const [agreement, setAgreement] = useState([]);
  const [landlordData, setLandlordData] = useState([]);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const pathParts = window.location.pathname
    .replace("/agreement/", "")
    .split("/");
  const tenantId = pathParts[0];
  const flatId = pathParts[1];
  const agreementRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    dispatch(GetOneAgreement({ id: `${tenantId}/${flatId}` })).then((res) => {
      const responseData = res?.payload?.data;
      const agreementData = responseData?.data;
      setData(agreementData);
      setAgreement(responseData?.agreement);
      setLandlordData(agreementData?.[1]);
      setLoading(false);
    });
  }, [dispatch, tenantId, flatId]);

  // ── Opens a clean new window with just agreement HTML + all page styles ──
  const handlePrint = () => {
    if (!agreementRef.current) return;

    const stylesHtml = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((n) => n.outerHTML)
      .join("\n");

    const agreementHtml = agreementRef.current.outerHTML;

    const pw = window.open("", "_blank", "width=900,height=750");
    pw.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Rental Agreement – OTS</title>
      ${stylesHtml}
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Serif+4:wght@300;400;600&display=swap');
        body { margin:0; padding:32px 48px; background:#fff !important; }
        .hideInPrint { display:none !important; }
        .MuiPaper-root { box-shadow:none !important; }
      </style>
    </head><body>
      ${agreementHtml}
      <script>
        window.onload = function(){
          setTimeout(function(){
            window.print();
            window.onafterprint = function(){ window.close(); };
          }, 700);
        };
      <\/script>
    </body></html>`);
    pw.document.close();
  };

  // Get condition labels from values
  const getConditionLabel = (value) => {
    const condition = landlordConditionOptions.find((c) => c.value === value);
    return condition ? condition.label : value;
  };

  // Parse landlord conditions
  const parsedConditions = React.useMemo(() => {
    const landlordConditions = data?.[0]?.propertyDetail?.[0]?.landlordConditions;
    if (!landlordConditions) return [];
    try {
      const conditions =
        typeof landlordConditions === "string"
          ? JSON.parse(landlordConditions)
          : landlordConditions;
      return Array.isArray(conditions) ? conditions : [];
    } catch { return []; }
  }, [data]);

  const getLeaseDuration = (v) => (
    { "6_months":"6 Months","11_months":"11 Months","1_year":"1 Year","2_years":"2 Years","flexible":"Flexible" }[v] || v || "11 Months"
  );
  const getNoticePeriod = (v) => (
    { "15_days":"15 Days","1_month":"1 Month","2_months":"2 Months","3_months":"3 Months" }[v] || v || "1 Month"
  );

 const calculateEndDate = () => {
  const moveInDate = agreement?.requestInfo?.moveInDate;

  if (!moveInDate) return "-";

  const startDate = moment(moveInDate, "DD-MM-YYYY"); // format bataya
  const duration = data?.propertyDetail?.[0]?.leaseDuration || "11_months";

  switch (duration) {
    case "6_months":
      return startDate.clone().add(6, "months").format("DD-MM-YYYY");

    case "11_months":
      return startDate.clone().add(11, "months").format("DD-MM-YYYY");

    case "1_year":
      return startDate.clone().add(1, "year").format("DD-MM-YYYY");

    case "2_years":
      return startDate.clone().add(2, "years").format("DD-MM-YYYY");

    default:
      return startDate.clone().add(11, "months").format("DD-MM-YYYY");
  }
};

  if (loading) {
    return (
      <>
        <Wrapper />
        <div className={`agreement-page ${open ? "sidebar-open" : "sidebar-closed"}`}>
          <Box sx={{ display:"flex", justifyContent:"center", m:10 }}>
            <CircularProgress />
          </Box>
        </div>
      </>
    );
  }

  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <>
        <Wrapper />
        <div className={`agreement-page ${open ? "sidebar-open" : "sidebar-closed"}`}>
          <Breadcrumbs sx={{ mb:2 }}>
            <Link to="/landlord-dashboard">Home</Link>
            <Link to="/my-tenants">My Tenants</Link>
            <Typography color="text.primary">Agreement</Typography>
          </Breadcrumbs>
          <Alert severity="info">Agreement not found or still being processed.</Alert>
        </div>
      </>
    );
  }

  const property = data?.[0]?.propertyDetail?.[0] || {};
  const acceptedDate = data?.acceptedDate || data?.paymentDate || data?.[0]?.acceptedDate;

  /* ───────────────────── All agreement CSS ───────────────────── */
  const agreementCss = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Serif+4:ital,wght@0,300;0,400;0,600;1,300&display=swap');

    .ots-agreement {
      font-family: 'Source Serif 4', Georgia, serif;
      color: #1a1a2e;
      background: #fff;
    }

    /* Header */
    .ots-header {
      background: linear-gradient(135deg, #0f2c5c 0%, #1a4a8a 55%, #0f2c5c 100%);
      padding: 36px 48px 28px;
      text-align: center;
      position: relative;
      overflow: hidden;
    }
    .ots-header::before {
      content:'';
      position:absolute; inset:0;
      background: repeating-linear-gradient(45deg,transparent,transparent 18px,rgba(255,255,255,0.03) 18px,rgba(255,255,255,0.03) 19px);
    }
    .ots-header-title {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 30px; font-weight: 700;
      color: #fff; letter-spacing: 5px;
      text-transform: uppercase; margin: 0 0 6px;
      position: relative;
    }
    .ots-header-sub {
      font-size: 12px; color: rgba(255,255,255,0.65);
      letter-spacing: 3px; text-transform: uppercase;
      position: relative;
    }
    .ots-header-rule {
      width: 60px; height: 2px;
      background: linear-gradient(90deg,transparent,#c9a84c,transparent);
      margin: 14px auto 10px; position: relative;
    }
    .ots-meta {
      display: flex; justify-content: center; gap: 36px;
      margin-top: 14px; position: relative;
    }
    .ots-meta-item { color: rgba(255,255,255,0.8); font-size: 12px; letter-spacing:.5px; }
    .ots-meta-item strong { color: #f0d080; }

    /* Body */
    .ots-body { padding: 4px 40px 36px; }

    /* Section heading */
    .ots-section { margin: 26px 0; }
    .ots-section-title {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 11px; font-weight: 700;
      letter-spacing: 3.5px; text-transform: uppercase;
      color: #0f2c5c;
      border-bottom: 2px solid #c9a84c;
      padding-bottom: 7px; margin-bottom: 16px;
      display: flex; align-items: center; gap: 9px;
    }
    .ots-section-title::before {
      content:''; display:inline-block;
      width:4px; height:16px;
      background:#c9a84c; border-radius:2px; flex-shrink:0;
    }

    /* Party cards */
    .ots-parties { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:16px; }
    .ots-party-card {
      border:1px solid #cdd5e8; border-radius:7px;
      padding:20px 20px 14px; background:#f7f9ff;
      position:relative; margin-top:12px;
    }
    .ots-party-label {
      position:absolute; top:-11px; left:16px;
      background:#0f2c5c; color:#fff;
      font-size:9px; font-weight:700; letter-spacing:2px;
      padding:3px 12px; border-radius:3px; text-transform:uppercase;
    }
    .ots-field { margin-bottom:10px; }
    .ots-field:last-child { margin-bottom:0; }
    .ots-field-label { font-size:10px; color:#7a88aa; letter-spacing:1px; text-transform:uppercase; margin-bottom:2px; }
    .ots-field-value { font-size:14px; font-weight:600; color:#1a1a2e; }

    /* Property grid */
    .ots-prop-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:10px; }
    .ots-prop-item { background:#f7f9ff; border:1px solid #dde5f5; border-radius:6px; padding:11px 14px; }
    .ots-prop-item.full { grid-column:1/-1; }

    /* Financial grid */
    .ots-fin-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(165px,1fr)); gap:10px; }
    .ots-fin-item {
      background:linear-gradient(135deg,#f0f4ff 0%,#f8faff 100%);
      border:1px solid #dde5f5; border-left:3px solid #0f2c5c;
      border-radius:0 6px 6px 0; padding:12px 14px;
    }
    .ots-fin-amount { font-size:14px; font-weight:700; color:#0f2c5c; margin-top:4px; }
    .ots-fin-amount.gold { color:#a07a10; }

    /* Lease grid */
    .ots-lease-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(165px,1fr)); gap:10px; }
    .ots-lease-item {
      text-align:center; background:#fff;
      border:1px solid #dde5f5; border-radius:6px; padding:14px 10px;
    }
    .ots-lease-item .val { font-size:14px; font-weight:700; color:#0f2c5c; margin-top:5px; }

    /* Terms list */
    .ots-terms-list { list-style:none; padding:0; margin:0; border:1px solid #e8edf8; border-radius:6px; overflow:hidden; }
    .ots-terms-list li {
      display:flex; align-items:flex-start; gap:12px;
      padding:10px 16px; border-bottom:1px solid #eef1f8;
      font-size:13px; line-height:1.65; background:#fff;
    }
    .ots-terms-list li:nth-child(even) { background:#f9faff; }
    .ots-terms-list li:last-child { border-bottom:none; }
    .ots-term-num {
      min-width:22px; height:22px;
      background:#0f2c5c; color:#fff; border-radius:50%;
      font-size:10px; font-weight:700;
      display:flex; align-items:center; justify-content:center;
      flex-shrink:0; margin-top:1px;
    }

    /* Extra conditions */
    .ots-extras {
      margin-top:14px; padding:14px 18px;
      background:#fffbf0; border:1px solid #e8d89a; border-radius:6px;
    }
    .ots-extras-label {
      font-size:10px; font-weight:700; letter-spacing:2px;
      color:#8a6a00; text-transform:uppercase; margin-bottom:10px;
    }
    .ots-extra-row { display:flex; align-items:flex-start; gap:8px; padding:5px 0; font-size:13px; }
    .ots-check { color:#1a7a3c; font-size:15px; flex-shrink:0; margin-top:1px; }

    /* Signatures — BOTH IN ONE ROW */
    .ots-sig-row { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:20px; }
    .ots-sig-box {
      border:1px solid #cdd5e8; border-radius:7px;
      padding:22px 18px 16px; text-align:center;
      background:#fafbff; position:relative; margin-top:12px;
    }
    .ots-sig-badge {
      position:absolute; top:-11px; left:50%; transform:translateX(-50%);
      background:#c9a84c; color:#fff;
      font-size:9px; font-weight:700; letter-spacing:2px;
      padding:3px 14px; border-radius:3px; text-transform:uppercase; white-space:nowrap;
    }
    .ots-sig-img {
      max-width:180px; max-height:65px;
      border:1px solid #e0e8f5; border-radius:4px;
      background:#fff; padding:4px;
      display:block; margin:10px auto 6px;
    }
    .ots-sig-name { font-size:14px; font-weight:700; color:#0f2c5c; }
    .ots-sig-date { font-size:11px; color:#7a8aaa; margin-top:3px; }
    .ots-sig-empty { padding:16px 0; color:#aab; }

    /* Effective banner */
    .ots-effective {
      background:linear-gradient(90deg,#eaf5ee,#f0faf3);
      border:1px solid #b2d8bf; border-left:4px solid #2e7d52;
      border-radius:6px; padding:12px 20px; margin-top:18px;
      text-align:center; font-size:13px; color:#1a5c38; font-weight:600;
    }

    /* Verified alert */
    .ots-sig-alert {
      background:#eaf5ee; border:1px solid #b2d8bf;
      border-left:4px solid #2e7d52; border-radius:6px;
      padding:10px 16px; margin-bottom:16px;
      font-size:13px; color:#1a5c38; font-weight:600;
    }

    /* Footer */
    .ots-footer {
      background:#0f2c5c; padding:14px 40px; text-align:center;
    }
    .ots-footer p { color:rgba(255,255,255,0.55); font-size:11px; margin:2px 0; letter-spacing:.5px; }

    /* ─────────── Responsive page shell ───────────
       Keeps the agreement clear of the fixed sidebar and lets it
       reflow smoothly the moment the sidebar is opened or closed. */
    .agreement-page {
      box-sizing: border-box;
      float: none !important;
      width: auto !important;
      max-width: 100%;
      margin: 10px 20px 28px !important;
      transition: margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1);
    }
    .agreement-page.sidebar-open   { margin-left: 262px !important; }
    .agreement-page.sidebar-closed { margin-left: 88px  !important; }

    @media (max-width: 1024px) {
      .agreement-page { margin: 10px 15px 24px !important; }
      .agreement-page.sidebar-open   { margin-left: 255px !important; }
      .agreement-page.sidebar-closed { margin-left: 80px  !important; }
    }
    /* Below 768px the drawer becomes a temporary overlay, so no offset */
    @media (max-width: 767px) {
      .agreement-page,
      .agreement-page.sidebar-open,
      .agreement-page.sidebar-closed { margin: 10px !important; }
    }

    /* Document never spills past its column */
    .ots-agreement { max-width: 100%; overflow-wrap: anywhere; }
    .ots-meta { flex-wrap: wrap; }

    /* Narrow-column tuning */
    @media (max-width: 600px) {
      .ots-header { padding: 24px 18px 20px; }
      .ots-header-title { font-size: 21px; letter-spacing: 3px; }
      .ots-header-sub { font-size: 10px; letter-spacing: 2px; }
      .ots-meta { flex-direction: column; align-items: center; gap: 6px; }
      .ots-body { padding: 4px 16px 26px; }
      .ots-section { margin: 20px 0; }
      .ots-terms-list li { padding: 10px 12px; font-size: 12px; gap: 9px; }
      .ots-extras { padding: 12px 14px; }
      .ots-effective { padding: 12px 14px; font-size: 12px; }
      .ots-footer { padding: 14px 16px; }
    }

    /* Printed sheet keeps its fixed column layout */
    @media print {
      .ots-parties, .ots-sig-row { grid-template-columns: 1fr 1fr; }
      .ots-prop-grid { grid-template-columns: repeat(3,1fr); }
      .ots-fin-grid, .ots-lease-grid { grid-template-columns: repeat(4,1fr); }
    }

    /* Print colors preserved */
    @media print {
      .hideInPrint { display:none !important; }
      .ots-header,.ots-footer,.ots-fin-item {
        -webkit-print-color-adjust:exact;
        print-color-adjust:exact;
      }
    }
  `;

  return (
    <>
      <Wrapper />
      <div className={`agreement-page ${open ? "sidebar-open" : "sidebar-closed"}`}>

        {/* ── Top action bar ── */}
        <Box
          sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:1.5, mb:3 }}
          className="hideInPrint"
        >
          <Breadcrumbs>
            <Link to="/landlord-dashboard" style={{ color:"var(--owner-primary)", textDecoration:"none" }}>Home</Link>
            <Link to="/my-tenants" style={{ color:"var(--owner-primary)", textDecoration:"none" }}>My Tenants</Link>
            <Typography color="text.primary" fontWeight={600}>Agreement</Typography>
          </Breadcrumbs>

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              background:"linear-gradient(135deg,#0f2c5c 0%,#1a4a8a 100%)",
              borderRadius:2, px:3, py:1.2,
              fontWeight:700, letterSpacing:.8,
              textTransform:"none", fontSize:14,
              boxShadow:"0 4px 14px rgba(15,44,92,0.35)",
              "&:hover":{ background:"linear-gradient(135deg,#0a1f42 0%,#123a72 100%)" },
            }}
          >
            Save / Print PDF
          </Button>
        </Box>

        {/* ── Styles + Document ── */}
        <style>{agreementCss}</style>

        <Paper
          ref={agreementRef}
          elevation={4}
          sx={{ width:"100%", maxWidth:820, mx:"auto", borderRadius:2, overflow:"hidden", mb:4 }}
        >
          <div className="ots-agreement">

            {/* ════ HEADER ════ */}
            <div className="ots-header">
              <div className="ots-header-title">Rental Agreement</div>
              <div className="ots-header-rule" />
              <div className="ots-header-sub">Owner Tenant Solution &nbsp;·&nbsp; OTS</div>
              <div className="ots-meta">
                <div className="ots-meta-item">
                  Date:&nbsp;<strong>{moment(acceptedDate).format("DD MMMM YYYY")}</strong>
                </div>
                <div className="ots-meta-item">
                  Ref&nbsp;#:&nbsp;<strong>{(property?._id || data?.[0]?._id)?.slice(-8).toUpperCase()}</strong>
                </div>
              </div>
            </div>

            {/* ════ BODY ════ */}
            <div className="ots-body">

              {/* PARTIES */}
              <div className="ots-section">
                <div className="ots-section-title">Parties to the Agreement</div>
                <div className="ots-parties">

                  {/* Owner */}
                  <div className="ots-party-card">
                    <div className="ots-party-label">Owner — First Party</div>
                    <div className="ots-field">
                      <div className="ots-field-label">Full Name</div>
                      <div className="ots-field-value">{landlordData?.ownerName || "—"}</div>
                    </div>
                    <div className="ots-field">
                      <div className="ots-field-label">Father's Name</div>
                      <div className="ots-field-value">
                        {validateUser?.UserValidate?.data?.user?.fatherName || "—"}
                      </div>
                    </div>
                    <div className="ots-field">
                      <div className="ots-field-label">CNIC</div>
                      <div className="ots-field-value">
                        {validateUser?.UserValidate?.data?.user?.cnic || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Tenant */}
                  <div className="ots-party-card">
                    <div className="ots-party-label">Tenant — Second Party</div>
                    <div className="ots-field">
                      <div className="ots-field-label">Full Name</div>
                      <div className="ots-field-value">{data?.[0]?.name}</div>
                    </div>
                    <div className="ots-field">
                      <div className="ots-field-label">Father's Name</div>
                      <div className="ots-field-value">{data?.[0]?.fatherName}</div>
                    </div>
                    <div className="ots-field">
                      <div className="ots-field-label">CNIC</div>
                      <div className="ots-field-value">{data?.[0]?.cnicNo}</div>
                    </div>
                  </div>

                </div>
              </div>

              {/* PROPERTY */}
              <div className="ots-section">
                <div className="ots-section-title">Property Details</div>
                <div className="ots-prop-grid">
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Property Name</div>
                    <div className="ots-field-value">{property?.flatName}</div>
                  </div>
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Unit / Flat No.</div>
                    <div className="ots-field-value">{property?.flatNumber}</div>
                  </div>
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Floor</div>
                    <div className="ots-field-value">{property?.flatFloor}</div>
                  </div>
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Bedrooms</div>
                    <div className="ots-field-value">{property?.flatRooms}</div>
                  </div>
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Bathrooms</div>
                    <div className="ots-field-value">{property?.flatToilet}</div>
                  </div>
                  <div className="ots-prop-item">
                    <div className="ots-field-label">Kitchen</div>
                    <div className="ots-field-value">{property?.flatKitchen}</div>
                  </div>
                  <div className="ots-prop-item full">
                    <div className="ots-field-label">Complete Address</div>
                    <div className="ots-field-value">{property?.propertyAddress || property?.mapAddress}</div>
                  </div>
                </div>
              </div>

              {/* FINANCIAL */}
              <div className="ots-section">
                <div className="ots-section-title">Financial Terms</div>
                <div className="ots-fin-grid">
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Monthly Rent</div>
                    <div className="ots-fin-amount gold">PKR {property?.flatRent?.toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Security Deposit</div>
                    <div className="ots-fin-amount">PKR {property?.flatAdvance?.toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Maintenance</div>
                    <div className="ots-fin-amount">PKR {(agreement?.financialTerms?.maintenanceCharges || 0).toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Security Charges</div>
                    <div className="ots-fin-amount">PKR {(agreement?.financialTerms?.securityCharges || 0).toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Trash Charges</div>
                    <div className="ots-fin-amount">PKR {(agreement?.financialTerms?.trashCharges || 0).toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Water Charges</div>
                    <div className="ots-fin-amount">PKR {(agreement?.financialTerms?.waterCharges || 0).toLocaleString()}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Rent Due Date</div>
                    <div className="ots-fin-amount" style={{fontSize:12}}>{property?.rentDueDate || 5}th of each month</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Late Fee</div>
                    <div className="ots-fin-amount" style={{fontSize:11}}>{describeLatePolicy(resolveLatePolicy(property)) || "No late fee"}</div>
                  </div>
                  <div className="ots-fin-item">
                    <div className="ots-field-label">Yearly Increase</div>
                    <div className="ots-fin-amount">{property?.rentIncrease || 10}%</div>
                  </div>
                </div>
              </div>
{console.log(moment(agreement?.requestInfo?.moveInDate).format("DD-MM-YYYY"),"agreementagreement")}
              {/* LEASE */}
              <div className="ots-section">
                <div className="ots-section-title">Lease Period</div>
                <div className="ots-lease-grid">
                  <div className="ots-lease-item">
                    <div className="ots-field-label">Duration</div>
                    <div className="val">{getLeaseDuration(property?.leaseDuration)}</div>
                  </div>
                  <div className="ots-lease-item">
                    <div className="ots-field-label">Start Date</div>
<div className="val">
  {agreement?.requestInfo?.moveInDate
    ? moment(agreement.requestInfo.moveInDate, "DD-MM-YYYY").format("DD-MM-YYYY")
    : "-"}
</div>
                  </div>
                  <div className="ots-lease-item">
                    <div className="ots-field-label">End Date</div>
                    <div className="val">{calculateEndDate()}</div>
                  </div>
                  <div className="ots-lease-item">
                    <div className="ots-field-label">Notice Period</div>
                    <div className="val">{getNoticePeriod(property?.noticePeriod)}</div>
                  </div>
                </div>
              </div>

              {/* TERMS */}
              <div className="ots-section">
                <div className="ots-section-title">Terms &amp; Conditions</div>
                <ul className="ots-terms-list">
                  {[
                    `The Tenant agrees to pay the monthly rent of PKR ${property?.flatRent?.toLocaleString()} on or before the ${property?.rentDueDate || 5}th of every month.`,
                    `Security deposit of PKR ${property?.flatAdvance?.toLocaleString()} has been paid and will be refunded within 30 days of vacating, subject to property condition.`,
                    `All utility bills (electricity, gas, water) shall be paid by the Tenant.`,
                    `The Tenant shall not challenge the ownership of the property.`,
                    `The Tenant shall maintain the property in good condition and shall not make any structural changes without written permission from the Owner.`,
                    `Either party must serve ${getNoticePeriod(property?.noticePeriod)} prior notice before vacating or terminating the agreement.`,
                    `Upon renewal, rent will be increased by ${property?.rentIncrease || 10}% as per mutual agreement.`,
                    ...(describeLatePolicy(resolveLatePolicy(property))
                      ? [`Late payment will incur a late fee: ${describeLatePolicy(resolveLatePolicy(property))}.`]
                      : []),
                  ].map((term, i) => (
                    <li key={i}>
                      <span className="ots-term-num">{i + 1}</span>
                      <span>{term}</span>
                    </li>
                  ))}
                </ul>

                {(parsedConditions.length > 0 || property?.customConditions) && (
                  <div className="ots-extras">
                    <div className="ots-extras-label">Additional Conditions — set by Owner</div>
                    {parsedConditions.map((c) => (
                      <div key={c} className="ots-extra-row">
                        <span className="ots-check">✓</span>
                        <span>{getConditionLabel(c)}</span>
                      </div>
                    ))}
                    {property?.customConditions && (
                      <p style={{ fontStyle:"italic", fontSize:13, color:"#555", margin:"8px 0 0" }}>
                        {property.customConditions}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* SIGNATURES — both in one row */}
              <div className="ots-section">
                <div className="ots-section-title">Digital Signatures</div>

                {(landlordData?.signature || data?.[0]?.signature) && (
                  <div className="ots-sig-alert">
                    ✓ &nbsp;Both signatures are digitally captured and legally binding as per OTS registration.
                  </div>
                )}

                <div className="ots-sig-row">
                  {/* Owner signature */}
                  <div className="ots-sig-box">
                    <div className="ots-sig-badge">Owner — First Party</div>
                    {landlordData?.signature ? (
                      <>
                        <img src={landlordData.signature} alt="Owner Signature" className="ots-sig-img" />
                        <div className="ots-sig-name">{landlordData?.ownerName || landlordData?.userName}</div>
                        <div className="ots-sig-date">
                          Signed: {moment(data?.acceptedDate || data?.paymentDate).format("DD MMMM YYYY")}
                        </div>
                      </>
                    ) : (
                      <div className="ots-sig-empty">
                        <DrawIcon style={{ fontSize:34, opacity:0.3 }} />
                        <div style={{ fontSize:12, marginTop:4 }}>Signature not available</div>
                      </div>
                    )}
                  </div>

                  {/* Tenant signature */}
                  <div className="ots-sig-box">
                    <div className="ots-sig-badge">Tenant — Second Party</div>
                    {data?.[0]?.signature ? (
                      <>
                        <img src={data?.[0]?.signature} alt="Tenant Signature" className="ots-sig-img" />
                        <div className="ots-sig-name">{data?.[0]?.name}</div>
                        <div className="ots-sig-date">
                          Signed: {moment(data?.acceptedDate || data?.paymentDate).format("DD MMMM YYYY")}
                        </div>
                      </>
                    ) : (
                      <div className="ots-sig-empty">
                        <DrawIcon style={{ fontSize:34, opacity:0.3 }} />
                        <div style={{ fontSize:12, marginTop:4 }}>Signature not available</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Effective date */}
                {(data?.acceptedDate || data?.paymentDate) && (
                  <div className="ots-effective">
                    ✓ &nbsp; Agreement Effective Date: &nbsp;
                    <span style={{ color:"#0f5c2e" }}>
                      {moment(data?.acceptedDate || data?.paymentDate).format("DD MMMM YYYY")}
                    </span>
                  </div>
                )}
              </div>

            </div>{/* /ots-body */}

            {/* FOOTER */}
            <div className="ots-footer">
              <p>This agreement is digitally generated through Owner Tenant Solution (OTS) and is legally valid.</p>
              <p>Generated on: {moment().format("DD MMMM YYYY, hh:mm A")}</p>
            </div>

          </div>{/* /ots-agreement */}
        </Paper>

      </div>

      {/* Preserve existing global print CSS */}
      <style>{`
        @media print {
          .hideInPrint { display: none !important; }
          .sidebar-open, .sidebar-closed { margin: 0 !important; padding: 0 !important; }
        }
      `}</style>
    </>
  );
}
////////////////////OLD Version////////////////
// import React, { useEffect, useState, useRef } from "react";
// import Wrapper from "../../../components/Wrapper";
// import Typography from "@mui/material/Typography";
// import {
//   Box,
//   Breadcrumbs,
//   CircularProgress,

//   Button,
//   Paper,
//   Grid,
//   Divider,
//   Chip,
//   Table,
//   TableBody,
//   TableCell,
//   TableRow,
//   Alert,
// } from "@mui/material";
// import title from "../../../components/title";
// import { GetOneAgreement } from "../../../redux/reducer/GetOneAgreement";
// import { useSelector, useDispatch } from "react-redux";
// import moment from "moment";
// import PrintIcon from "@mui/icons-material/Print";

// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import DrawIcon from "@mui/icons-material/Draw";
// import { Link } from "react-router-dom";
// import { landlordConditionOptions } from "../MyProperty/validationSchema";

// export default function Agreements() {
//   title("Agreement");
//   const open = useSelector((state) => state.sidebar.open);
//   const { validateUser } = useSelector((state) => state);

//   const [data, setData] = useState([]);
//   const [landlordData, setLandlordData] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const dispatch = useDispatch();
//   const pathParts = window.location.pathname.replace("/agreement/", "").split("/");
//   const tenantId = pathParts[0];
//   const flatId = pathParts[1];
//   const agreementRef = useRef(null);

//   useEffect(() => {
//     setLoading(true);
//     dispatch(GetOneAgreement({ id: `${tenantId}/${flatId}` })).then((res) => {
//       const responseData = res?.payload?.data;
//       const agreementData = responseData?.data;

//       setData(agreementData);
//       setLandlordData(agreementData?.[1]);
//       setLoading(false);
//     });
//   }, [dispatch, tenantId, flatId]);

//   const handlePrint = () => {
//     window.print();
//   };

//   // Get condition labels from values
//   const getConditionLabel = (value) => {
//     const condition = landlordConditionOptions.find((c) => c.value === value);
//     return condition ? condition.label : value;
//   };

//   // Parse landlord conditions
//   const parsedConditions = React.useMemo(() => {
//     const landlordConditions =
//       data?.[0]?.propertyDetail?.[0]?.landlordConditions;

//     if (!landlordConditions) return [];

//     try {
//       const conditions =
//         typeof landlordConditions === "string"
//           ? JSON.parse(landlordConditions)
//           : landlordConditions;

//       return Array.isArray(conditions) ? conditions : [];
//     } catch {
//       return [];
//     }
//   }, [data]);

//   // Get lease duration label
//   const getLeaseDuration = (value) => {
//     const options = {
//       "6_months": "6 Months",
//       "11_months": "11 Months",
//       "1_year": "1 Year",
//       "2_years": "2 Years",
//       flexible: "Flexible",
//     };
//     return options[value] || value || "11 Months";
//   };

//   // Get notice period label
//   const getNoticePeriod = (value) => {
//     const options = {
//       "15_days": "15 Days",
//       "1_month": "1 Month",
//       "2_months": "2 Months",
//       "3_months": "3 Months",
//     };
//     return options[value] || value || "1 Month";
//   };

//   // Calculate lease end date
//   const calculateEndDate = () => {
//     const startDate = moment(data?.acceptedDate);
//     const duration = data?.propertyDetail?.[0]?.leaseDuration || "11_months";

//     switch (duration) {
//       case "6_months":
//         return startDate.add(6, "months").format("DD-MM-YYYY");
//       case "11_months":
//         return startDate.add(11, "months").format("DD-MM-YYYY");
//       case "1_year":
//         return startDate.add(1, "year").format("DD-MM-YYYY");
//       case "2_years":
//         return startDate.add(2, "years").format("DD-MM-YYYY");
//       default:
//         return startDate.add(11, "months").format("DD-MM-YYYY");
//     }
//   };

//   if (loading) {
//     return (
//       <>
//         <Wrapper />
//         <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
//           <Box sx={{ display: "flex", justifyContent: "center", m: 10 }}>
//             <CircularProgress />
//           </Box>
//         </div>
//       </>
//     );
//   }

//   if (!data || (Array.isArray(data) && data.length === 0)) {
//     return (
//       <>
//         <Wrapper />
//         <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
//           <Breadcrumbs sx={{ mb: 2 }}>
//             <Link to="/landlord-dashboard">Home</Link>
//             <Link to="/my-tenants">My Tenants</Link>
//             <Typography color="text.primary">Agreement</Typography>
//           </Breadcrumbs>
//           <Alert severity="info">
//             Agreement not found or still being processed.
//           </Alert>
//         </div>
//       </>
//     );
//   }

//   const property = data?.[0]?.propertyDetail?.[0] || {};

//   return (
//     <>
//       <Wrapper />
//       <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
//         {/* Action Buttons */}
//         <Box
//           sx={{ display: "flex", gap: 2, mb: 3, justifyContent: "flex-end" }}
//           className="hideInPrint"
//         >
//           <Button
//             variant="outlined"
//             startIcon={<PrintIcon />}
//             onClick={handlePrint}
//           >
//             Print Agreement
//           </Button>
//         </Box>

//         {/* Agreement Document */}
//         <Paper
//           ref={agreementRef}
//           elevation={3}
//           sx={{
//             p: 4,
//             maxWidth: 800,
//             mx: "auto",
//             "@media print": {
//               boxShadow: "none",
//               p: 2,
//             },
//           }}
//         >
//           {/* Header */}
//           <Box
//             sx={{
//               textAlign: "center",
//               mb: 2,
//               borderBottom: "2px solid #1976d2",
//               pb: 2,
//             }}
//           >
//             <Typography variant="h4" fontWeight="bold" color="primary">
//               RENTAL AGREEMENT
//             </Typography>
//             <Typography variant="subtitle1" color="text.secondary">
//               Online Tenant System (OTS)
//             </Typography>
//             <Chip
//               icon={<CheckCircleIcon />}
//               label="Digitally Generated Agreement"
//               color="success"
//               size="small"
//               sx={{ mt: 1 }}
//               className="hideInPrint"
//             />
//           </Box>

//           {/* Parties Section */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="primary"
//               gutterBottom
//             >
//               PARTIES TO THE AGREEMENT
//             </Typography>
//             <Grid container spacing={3}>
//               {/* Landlord */}
//               <Grid item xs={12} md={6}>
//                 <Paper variant="outlined" sx={{ p: 2 }}>
//                   <Typography
//                     variant="subtitle2"
//                     color="text.secondary"
//                     gutterBottom
//                   >
//                     OWNER (First Party)
//                   </Typography>
//                   <Table size="small">
//                     <TableBody>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>Name:</TableCell>
//                         <TableCell
//                           sx={{ border: 0, py: 0.5, fontWeight: "bold" }}
//                         >
//                           {landlordData?.ownerName}
//                         </TableCell>
//                       </TableRow>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           Father Name:
//                         </TableCell>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           {validateUser?.UserValidate?.data?.user?.fatherName}
//                         </TableCell>
//                       </TableRow>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>CNIC:</TableCell>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           {validateUser?.UserValidate?.data?.user?.cnic}
//                         </TableCell>
//                       </TableRow>
//                     </TableBody>
//                   </Table>
//                 </Paper>
//               </Grid>

//               {/* Tenant */}
//               <Grid item xs={12} md={6}>
//                 <Paper variant="outlined" sx={{ p: 2 }}>
//                   <Typography
//                     variant="subtitle2"
//                     color="text.secondary"
//                     gutterBottom
//                   >
//                     TENANT (Second Party)
//                   </Typography>
//                   <Table size="small">
//                     <TableBody>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>Name:</TableCell>
//                         <TableCell
//                           sx={{ border: 0, py: 0.5, fontWeight: "bold" }}
//                         >
//                           {data?.[0]?.name}
//                         </TableCell>
//                       </TableRow>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           Father Name:
//                         </TableCell>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           {data?.[0]?.fatherName}
//                         </TableCell>
//                       </TableRow>
//                       <TableRow>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>CNIC:</TableCell>
//                         <TableCell sx={{ border: 0, py: 0.5 }}>
//                           {data?.[0]?.cnicNo}
//                         </TableCell>
//                       </TableRow>
//                     </TableBody>
//                   </Table>
//                 </Paper>
//               </Grid>
//             </Grid>
//           </Box>

//           <Divider sx={{ my: 3 }} />

//           {/* Property Details */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="primary"
//               gutterBottom
//             >
//               PROPERTY DETAILS
//             </Typography>
//             <Grid container spacing={2}>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Property Name
//                 </Typography>
//                 <Typography fontWeight="bold">{property?.flatName}</Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Unit/Flat No.
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {property?.flatNumber}
//                 </Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Floor
//                 </Typography>
//                 <Typography fontWeight="bold">{property?.flatFloor}</Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Bedrooms
//                 </Typography>
//                 <Typography fontWeight="bold">{property?.flatRooms}</Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Bathrooms
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {property?.flatToilet}
//                 </Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Kitchen
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {property?.flatKitchen}
//                 </Typography>
//               </Grid>
//               <Grid item xs={12}>
//                 <Typography variant="body2" color="text.secondary">
//                   Address
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {property?.propertyAddress || property?.mapAddress}
//                 </Typography>
//               </Grid>
//             </Grid>
//           </Box>

//           <Divider sx={{ my: 3 }} />

//           {/* Financial Terms */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="primary"
//               gutterBottom
//             >
//               FINANCIAL TERMS
//             </Typography>
//             <Paper variant="outlined" sx={{ p: 2 }}>
//               <Grid container spacing={2}>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Monthly Rent
//                   </Typography>
//                   <Typography
//                     variant="h6"
//                     fontWeight="bold"
//                     color="success.main"
//                   >
//                     PKR {property?.flatRent?.toLocaleString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Security Deposit
//                   </Typography>
//                   <Typography variant="h6" fontWeight="bold">
//                     PKR {property?.flatAdvance?.toLocaleString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Maintenance
//                   </Typography>
//                   <Typography variant="h6" fontWeight="bold">
//                     PKR {property?.flatMaintananceCharges?.toLocaleString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Security Charges
//                   </Typography>
//                   <Typography variant="h6" fontWeight="bold">
//                     PKR {property?.flatSecurityCharges?.toLocaleString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Trash Charges
//                   </Typography>
//                   <Typography fontWeight="bold">
//                     PKR {property?.flatTrashCharges?.toLocaleString()}
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Rent Due Date
//                   </Typography>
//                   <Typography fontWeight="bold">
//                     {property?.rentDueDate || 5}th of each month
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Late Fee
//                   </Typography>
//                   <Typography fontWeight="bold">
//                     PKR {property?.lateChargesPerDay || 0}/day after{" "}
//                     {property?.rentRelaxationDays || 5} days
//                   </Typography>
//                 </Grid>
//                 <Grid item xs={6} md={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Yearly Increase
//                   </Typography>
//                   <Typography fontWeight="bold">
//                     {property?.rentIncrease || 10}%
//                   </Typography>
//                 </Grid>
//               </Grid>
//             </Paper>
//           </Box>

//           <Divider sx={{ my: 3 }} />

//           {/* Lease Period */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="primary"
//               gutterBottom
//             >
//               LEASE PERIOD
//             </Typography>
//             <Grid container spacing={2}>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Lease Duration
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {getLeaseDuration(property?.leaseDuration)}
//                 </Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Start Date
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {moment(data?.acceptedDate).format("DD-MM-YYYY")}
//                 </Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   End Date
//                 </Typography>
//                 <Typography fontWeight="bold">{calculateEndDate()}</Typography>
//               </Grid>
//               <Grid item xs={6} md={4}>
//                 <Typography variant="body2" color="text.secondary">
//                   Notice Period
//                 </Typography>
//                 <Typography fontWeight="bold">
//                   {getNoticePeriod(property?.noticePeriod)}
//                 </Typography>
//               </Grid>
//             </Grid>
//           </Box>

//           <Divider sx={{ my: 3 }} />

//           {/* Terms and Conditions */}
//           <Box sx={{ mb: 4 }}>
//             <Typography
//               variant="h6"
//               fontWeight="bold"
//               color="primary"
//               gutterBottom
//             >
//               TERMS AND CONDITIONS
//             </Typography>

//             {/* Standard Terms */}
//             <Box sx={{ mb: 2 }}>
//               <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
//                 Standard Terms:
//               </Typography>
//               <ol style={{ paddingLeft: 20, margin: 0 }}>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     The Tenant agrees to pay the monthly rent of PKR{" "}
//                     {property?.flatRent?.toLocaleString()} on or before the{" "}
//                     {property?.rentDueDate || 5}th of every month.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     Security deposit of PKR{" "}
//                     {property?.flatAdvance?.toLocaleString()} has been paid by
//                     the Tenant and will be refunded within 30 days of vacating,
//                     subject to property condition.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     All utility bills (electricity, gas, water) shall be paid by
//                     the Tenant.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     The Tenant shall not challenge the ownership of the
//                     property.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     The Tenant shall maintain the property in good condition and
//                     shall not make any structural changes without written
//                     permission.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     Either party must serve{" "}
//                     {getNoticePeriod(property?.noticePeriod)} prior notice
//                     before vacating or terminating the agreement.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     Upon renewal, rent will be increased by{" "}
//                     {property?.rentIncrease || 10}% as per mutual agreement.
//                   </Typography>
//                 </li>
//                 <li style={{ marginBottom: 8 }}>
//                   <Typography variant="body2">
//                     Late payment will incur a penalty of PKR{" "}
//                     {property?.lateChargesPerDay || 0} per day after{" "}
//                     {property?.rentRelaxationDays || 5} grace days.
//                   </Typography>
//                 </li>
//               </ol>
//             </Box>

//             {/* Landlord's Specific Conditions */}
//             {(parsedConditions.length > 0 || property?.customConditions) && (
//               <Box sx={{ mt: 3 }}>
//                 <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
//                   Additional Conditions (Set by Landlord):
//                 </Typography>
//                 <Box sx={{ pl: 2 }}>
//                   {parsedConditions.map((condition, index) => (
//                     <Box
//                       key={condition}
//                       sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
//                     >
//                       <CheckCircleIcon
//                         sx={{
//                           fontSize: 16,
//                           color: "success.main",
//                           mr: 1,
//                           mt: 0.3,
//                         }}
//                       />
//                       <Typography variant="body2">
//                         {getConditionLabel(condition)}
//                       </Typography>
//                     </Box>
//                   ))}
//                   {property?.customConditions && (
//                     <Box sx={{ mt: 1 }}>
//                       <Typography variant="body2" sx={{ fontStyle: "italic" }}>
//                         {property.customConditions}
//                       </Typography>
//                     </Box>
//                   )}
//                 </Box>
//               </Box>
//             )}
//           </Box>

//           <Divider sx={{ my: 3 }} />

//           {/* Digital Signatures Section */}
//           <Box sx={{ mb: 4 }}>
//             <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
//               DIGITAL SIGNATURES
//             </Typography>

//             {(landlordData?.signature || data?.[0]?.signature) && (
//               <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
//                 Both signatures are legally binding as per registration.
//               </Alert>
//             )}

//             <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
//               {/* Landlord Signature */}
//               <Box sx={{ flex: 1 }}>
//                 <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
//                   <Typography variant="subtitle2" color="text.secondary" gutterBottom>
//                     OWNER (First Party)
//                   </Typography>

//                   {landlordData?.signature ? (
//                     <>
//                       <Box
//                         component="img"
//                         src={landlordData?.signature}
//                         alt="Landlord Signature"
//                         sx={{
//                           maxWidth: "100%",
//                           maxHeight: 80,
//                           border: "1px solid #e0e0e0",
//                           borderRadius: 1,
//                           bgcolor: "#fff",
//                           mb: 1,
//                         }}
//                       />
//                       <Typography variant="body2" fontWeight="bold">
//                         {landlordData?.ownerName || landlordData?.userName}
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Signed on: {moment(data?.acceptedDate || data?.paymentDate).format("DD-MM-YYYY")}
//                       </Typography>
//                     </>
//                   ) : (
//                     <Box sx={{ py: 2, color: "#999" }}>
//                       <DrawIcon sx={{ fontSize: 40, opacity: 0.5 }} />
//                       <Typography variant="body2">Signature not available</Typography>
//                     </Box>
//                   )}
//                 </Paper>
//               </Box>

//               {/* Tenant Signature */}
//               <Box sx={{ flex: 1 }}>
//                 <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
//                   <Typography variant="subtitle2" color="text.secondary" gutterBottom>
//                     TENANT (Second Party)
//                   </Typography>

//                   {data?.[0]?.signature ? (
//                     <>
//                       <Box
//                         component="img"
//                         src={data?.[0]?.signature}
//                         alt="Tenant Signature"
//                         sx={{
//                           maxWidth: "100%",
//                           maxHeight: 80,
//                           border: "1px solid #e0e0e0",
//                           borderRadius: 1,
//                           bgcolor: "#fff",
//                           mb: 1,
//                         }}
//                       />
//                       <Typography variant="body2" fontWeight="bold">
//                         {data?.[0]?.name}
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Signed on: {moment(data?.acceptedDate || data?.paymentDate).format("DD-MM-YYYY")}
//                       </Typography>
//                     </>
//                   ) : (
//                     <Box sx={{ py: 2, color: "#999" }}>
//                       <DrawIcon sx={{ fontSize: 40, opacity: 0.5 }} />
//                       <Typography variant="body2">Signature not available</Typography>
//                     </Box>
//                   )}
//                 </Paper>
//               </Box>
//             </Box>

//             {/* Agreement Effective Date */}
//             {(data?.acceptedDate || data?.paymentDate) && (
//               <Box sx={{ mt: 2, p: 2, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
//                 <Typography variant="body2" color="success.main">
//                   <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
//                   <strong>Agreement Effective Date:</strong> {moment(data?.acceptedDate || data?.paymentDate).format("DD MMMM YYYY")}
//                 </Typography>
//               </Box>
//             )}
//           </Box>

//           {/* Footer */}
//           <Box
//             sx={{
//               textAlign: "center",
//               mt: 4,
//               pt: 2,
//               borderTop: "1px solid #e0e0e0",
//             }}
//           >
//             <Typography variant="caption" color="text.secondary">
//               This agreement is digitally generated through Online Tenant System
//               (OTS).
//             </Typography>
//             <Typography
//               variant="caption"
//               display="block"
//               color="text.secondary"
//             >
//               Generated on: {moment().format("DD MMMM YYYY, hh:mm A")}
//             </Typography>
//           </Box>
//         </Paper>
//       </div>

//       {/* Print Styles */}
//       <style>
//         {`
//           @media print {
//             /* Hide sidebar and wrapper elements */
//             .hideInPrint,
//             .MuiDrawer-root,
//             .MuiDrawer-paper,
//             .MuiAppBar-root,
//             nav,
//             aside {
//               display: none !important;
//             }

//             /* Reset margins for content div */
//             .sidebar-open,
//             .sidebar-closed {
//               margin-left: 0 !important;
//               padding-left: 0 !important;
//               width: 100% !important;
//             }

//             /* Make body and html full width */
//             body, html {
//               margin: 0 !important;
//               padding: 0 !important;
//               width: 100% !important;
//               height: auto !important;
//             }

//             /* Center the agreement content */
//             .MuiPaper-root {
//               box-shadow: none !important;
//               margin: 0 auto !important;
//               max-width: 100% !important;
//               width: 100% !important;
//               padding: 20px !important;
//             }

//             /* Ensure all text is visible */
//             * {
//               -webkit-print-color-adjust: exact !important;
//               color-adjust: exact !important;
//             }

//             /* Page breaks */
//             .MuiDivider-root {
//               page-break-after: avoid;
//             }

//             /* Ensure tables don't break awkwardly */
//             table, tr, td, th {
//               page-break-inside: avoid;
//             }

//             /* Hide breadcrumbs in print */
//             .MuiBreadcrumbs-root {
//               display: none !important;
//             }

//             /* Adjust page margins */
//             @page {
//               margin: 0.5in;
//               size: A4;
//             }
//           }
//         `}
//       </style>
//     </>
//   );
// }
